import { useEffect } from 'react';

export type LegalPageKind = 'oauth' | 'privacy' | 'terms';

interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

const termsSections: LegalSection[] = [
  {
    heading: '1. Purpose of HatMail',
    paragraphs: [
      'HatMail is an internal email intelligence and workflow tool used by authorized Greeley Hat Works team members. It helps organize customer-service messages, surface relevant order context, draft suggested replies, route escalations, and track review activity.',
      'HatMail is not a public shopping tool, marketplace, payment service, legal service, or replacement for human review. It is designed to support internal customer-service work for Greeley Hat Works.',
    ],
  },
  {
    heading: '2. Authorized use',
    paragraphs: [
      'You may use HatMail only if you are authorized by Greeley Hat Works or Local Nerds. Use the tool only for legitimate Greeley Hat Works business purposes and only within the access level assigned to you.',
    ],
    bullets: [
      'Do not share PINs, session links, passwords, or account access.',
      'Do not access inboxes, customer records, attachments, or reports unless your role requires it.',
      'Do not copy, export, screenshot, or forward customer information outside approved business workflows.',
      'Do not use HatMail to test, reverse engineer, overload, bypass, or interfere with any system.',
    ],
  },
  {
    heading: '3. AI-assisted drafts',
    paragraphs: [
      'HatMail may classify messages, summarize context, and prepare suggested customer replies using automated systems. These outputs are assistance, not final decisions.',
      'Unless a specific workflow has been approved for automatic sending, an authorized human user is responsible for reviewing any draft before it is sent. The reviewing user is responsible for confirming accuracy, tone, pricing, order status, policy statements, attachments, recipients, and any commitment made to a customer.',
    ],
    bullets: [
      'Do not send legal, financial, employment, medical, or other regulated advice through HatMail unless the final message has been reviewed and approved by the appropriate decision maker.',
      'Do not promise refunds, discounts, rush timing, exceptions, repairs, replacements, or owner-level decisions unless those statements are approved by Greeley Hat Works.',
      'Escalate messages that involve complaints, disputes, chargebacks, legal threats, sensitive issues, unusual customer requests, or facts that cannot be verified.',
    ],
  },
  {
    heading: '4. Connected systems and data',
    paragraphs: [
      'HatMail may connect to business systems approved by Greeley Hat Works, including email, ecommerce, order-status, customer, analytics, and workflow systems. Information from those systems is used only to operate HatMail and support approved Greeley Hat Works workflows.',
      'You must follow all Greeley Hat Works policies, Local Nerds instructions, and applicable laws when using information shown in HatMail.',
    ],
  },
  {
    heading: '5. Confidentiality',
    paragraphs: [
      'HatMail may display confidential business information, customer communications, order information, attachments, internal notes, staff activity, and operational logs. You must protect that information and use it only for approved business purposes.',
      'If you believe information was accessed by the wrong person, shared outside approved channels, or handled incorrectly, notify your HatMail administrator or Local Nerds promptly.',
    ],
  },
  {
    heading: '6. Account security and audit activity',
    paragraphs: [
      'HatMail may log authentication events, staff selections, review actions, draft edits, sends, escalations, device identifiers, and other activity needed for security, accountability, troubleshooting, and service improvement.',
      'Greeley Hat Works and Local Nerds may suspend or limit access if an account appears compromised, misused, inactive, or no longer authorized.',
    ],
  },
  {
    heading: '7. Availability and changes',
    paragraphs: [
      'HatMail may be updated, interrupted, limited, or unavailable from time to time because of maintenance, vendor outages, internet issues, configuration changes, security events, or other causes. Local Nerds may change features, workflows, labels, integrations, and access controls as needed to operate and improve the service.',
    ],
  },
  {
    heading: '8. Intellectual property',
    paragraphs: [
      'HatMail software, interface design, workflows, prompts, documentation, and related materials are owned by Local Nerds or its licensors unless a written agreement says otherwise. Greeley Hat Works retains ownership of its business data, customer communications, brand materials, and operational content.',
    ],
  },
  {
    heading: '9. Disclaimers',
    paragraphs: [
      'HatMail is provided for internal operational support. To the fullest extent allowed by law, the service is provided without warranties of uninterrupted operation, error-free output, complete accuracy, or fitness for a particular purpose.',
      'AI-generated and automated outputs may be incomplete, outdated, incorrect, or inappropriate for a specific situation. Human review and business judgment remain required.',
    ],
  },
  {
    heading: '10. Limitation of liability',
    paragraphs: [
      'To the fullest extent allowed by law, Local Nerds will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages arising from HatMail use, including lost profits, lost revenue, loss of goodwill, business interruption, data loss, or customer claims, except where a separate written agreement states otherwise.',
    ],
  },
  {
    heading: '11. Conflicts with written agreements',
    paragraphs: [
      'If these Terms conflict with a signed agreement between Greeley Hat Works and Local Nerds, the signed agreement controls to the extent of the conflict.',
    ],
  },
  {
    heading: '12. Governing law and contact',
    paragraphs: [
      'These Terms are governed by Colorado law unless a written agreement states otherwise.',
      'Questions about HatMail access, use, or these Terms should be sent to your HatMail administrator or to Local Nerds through the support channel established for Greeley Hat Works.',
      'Greeley Hat Works contact: Jesse Sullivan. Mailing address: Greeley Hat Works, 2613 8th Ave, Greeley, CO 80631.',
    ],
  },
];

const privacySections: LegalSection[] = [
  {
    heading: '1. Scope',
    paragraphs: [
      'This Privacy Policy explains how information is collected, used, shared, and protected when authorized users access HatMail, an internal email intelligence and workflow tool operated for Greeley Hat Works by Local Nerds.',
      'HatMail is used to support Greeley Hat Works customer-service operations. It is not a public ecommerce checkout, payment processor, or consumer account portal.',
    ],
  },
  {
    heading: '2. Information HatMail processes',
    paragraphs: [
      'HatMail may process information provided by users, generated through use of the tool, or received from connected business systems approved by Greeley Hat Works.',
    ],
    bullets: [
      'User and staff information, such as name, email address, role, avatar, login method, PIN-based session details, device identifier, and admin status.',
      'Customer-service communications, such as email sender and recipient information, message bodies, subject lines, thread history, attachments, drafts, edits, sends, and escalations.',
      'Business context, such as order numbers, order status, production-stage context, product context, customer records, and other information made available by connected systems.',
      'Operational information, such as timestamps, audit logs, feedback, review activity, error states, health checks, and security events.',
    ],
  },
  {
    heading: '3. How information is used',
    paragraphs: [
      'HatMail uses information to operate, secure, monitor, and improve the service and to support approved Greeley Hat Works customer-service workflows.',
    ],
    bullets: [
      'Authenticate users and enforce role-based access.',
      'Show inbox queues, customer-service cards, order context, attachments, draft replies, and escalation options.',
      'Classify messages, summarize relevant context, prepare suggested responses, and support human review.',
      'Track activity for accountability, troubleshooting, reporting, quality review, and system reliability.',
      'Maintain security, detect misuse, diagnose errors, and comply with applicable legal or contractual obligations.',
    ],
  },
  {
    heading: '4. AI and service providers',
    paragraphs: [
      'HatMail may send relevant message content, business context, and user instructions to selected service providers so they can classify messages, generate draft replies, store data, host the application, monitor reliability, or provide other operational support.',
      'Service providers are used to operate HatMail for Greeley Hat Works and Local Nerds. HatMail data is not sold by Local Nerds, and HatMail is not used by Local Nerds for targeted advertising.',
    ],
  },
  {
    heading: '5. How information is shared',
    paragraphs: [
      'Information may be shared only as needed to operate HatMail, support Greeley Hat Works workflows, protect the service, or comply with legal obligations.',
    ],
    bullets: [
      'With authorized Greeley Hat Works users and administrators.',
      'With Local Nerds personnel and systems that operate, support, secure, or improve HatMail.',
      'With approved vendors and connected business systems used for hosting, authentication, email, AI processing, ecommerce context, storage, monitoring, and analytics.',
      'With legal, security, or compliance parties if required to protect rights, investigate misuse, respond to lawful requests, or comply with applicable law.',
    ],
  },
  {
    heading: '6. Data retention',
    paragraphs: [
      'HatMail retains information for as long as needed to operate the service, maintain auditability, support customer-service workflows, troubleshoot issues, comply with legal or contractual duties, and improve reliability. Retention periods may vary by data type, system configuration, business need, and legal requirement.',
    ],
  },
  {
    heading: '7. Security',
    paragraphs: [
      'HatMail uses administrative, technical, and organizational safeguards intended to protect information from unauthorized access, misuse, loss, and disclosure. These safeguards may include role-based access, authentication controls, audit logs, secure hosting, access restrictions, and operational monitoring.',
      'No system can guarantee perfect security. Users must protect their own access credentials and report suspected unauthorized access promptly.',
    ],
  },
  {
    heading: '8. User controls and requests',
    paragraphs: [
      'Authorized HatMail users may request access changes, corrections, or account removal through their HatMail administrator or Local Nerds support channel. Customer privacy requests related to Greeley Hat Works customer information should be submitted through the official Greeley Hat Works customer privacy or support channels.',
      'Depending on location and applicable law, individuals may have rights to access, correct, delete, receive a copy of, or opt out of certain uses of personal information. Requests will be handled according to the role of Greeley Hat Works, Local Nerds, and applicable service providers under the relevant law and written agreements.',
    ],
  },
  {
    heading: '9. Children',
    paragraphs: [
      'HatMail is an internal business tool. It is not intended for use by children or for collecting information directly from children.',
    ],
  },
  {
    heading: '10. Changes to this policy',
    paragraphs: [
      'This Privacy Policy may be updated from time to time to reflect changes in HatMail, connected systems, legal requirements, or business practices. The updated version will be posted in HatMail with a revised last updated date.',
    ],
  },
  {
    heading: '11. Contact',
    paragraphs: [
      'Questions about this Privacy Policy or HatMail data practices should be sent to your HatMail administrator or to Local Nerds through the support channel established for Greeley Hat Works.',
      'Greeley Hat Works contact: Jesse Sullivan. Mailing address: Greeley Hat Works, 2613 8th Ave, Greeley, CO 80631.',
      'Customer privacy requests should be routed through the official Greeley Hat Works customer support or privacy channel.',
    ],
  },
];

const oauthSections: LegalSection[] = [
  {
    heading: 'Purpose',
    paragraphs: [
      'HatMail for Greeley Hat Works is an internal mailbox workflow tool used by authorized Greeley Hat Works and Local Nerds operators to review, organize, and respond to Greeley Hat Works customer email.',
      'The application helps authorized users see mailbox activity, classify customer-service messages, review suggested replies, route escalations, and track operational activity for Greeley Hat Works.',
    ],
  },
  {
    heading: 'Who uses this app',
    paragraphs: [
      'HatMail is not a public customer portal, shopping tool, payment service, or consumer account system. Access is limited to approved Greeley Hat Works and Local Nerds users who support Greeley Hat Works customer-service operations.',
    ],
  },
  {
    heading: 'Google data use',
    paragraphs: [
      'HatMail connects to approved Greeley Hat Works Google mailboxes so authorized users can process customer-service email inside the HatMail workflow. Google user data is used only to operate HatMail for Greeley Hat Works and is not sold or used for advertising.',
    ],
  },
  {
    heading: 'Policies',
    paragraphs: [
      'Review the HatMail Privacy Policy and Terms of Service for details about data handling, user responsibilities, security, retention, and connected systems.',
    ],
  },
];

const pageContent = {
  oauth: {
    title: 'HatMail for Greeley Hat Works',
    intro:
      'HatMail for Greeley Hat Works is the internal mailbox workflow application used to support Greeley Hat Works customer-service operations.',
    sections: oauthSections,
  },
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This policy describes how HatMail handles information for authorized users and Greeley Hat Works customer-service workflows.',
    sections: privacySections,
  },
  terms: {
    title: 'Terms of Service',
    intro:
      'These terms govern authorized internal use of HatMail by Greeley Hat Works team members and administrators.',
    sections: termsSections,
  },
} satisfies Record<LegalPageKind, { title: string; intro: string; sections: LegalSection[] }>;

export default function LegalPage({ page }: { page: LegalPageKind }) {
  const content = pageContent[page];

  useEffect(() => {
    document.title = `${content.title} | HatMail`;
  }, [content.title]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <a href="/" className="flex items-center gap-2" aria-label="Back to HatMail">
            <span className="text-lg font-bold text-ghw-brown">GHW</span>
            <span className="text-lg font-light text-gray-400">/</span>
            <span className="text-lg font-semibold text-gray-800">HatMail</span>
          </a>
          <nav className="flex items-center gap-4 text-sm font-medium text-gray-500">
            {page !== 'oauth' && (
              <a href="/oauth-app" className="hover:text-ghw-brown">
                App Info
              </a>
            )}
            <a href="/" className="hover:text-ghw-brown">
              Back to HatMail
            </a>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-8">
          <p className="text-sm font-medium uppercase tracking-wide text-ghw-gold">HatMail</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-950">{content.title}</h1>
          <p className="mt-2 text-sm text-gray-500">Last updated: July 7, 2026</p>
          <p className="mt-5 text-base leading-7 text-gray-700">{content.intro}</p>

          <div className="mt-8 space-y-8">
            {content.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-lg font-semibold text-gray-950">{section.heading}</h2>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="mt-3 text-sm leading-7 text-gray-700">
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-gray-700">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {page === 'oauth' && (
            <div className="mt-8 flex flex-wrap gap-4 border-t border-gray-200 pt-6 text-sm font-medium">
              <a href="/privacy" className="text-ghw-brown hover:text-ghw-brown/80">
                Privacy Policy
              </a>
              <a href="/terms" className="text-ghw-brown hover:text-ghw-brown/80">
                Terms of Service
              </a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
